import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, AlertCircle, Grid3X3, RotateCcw, Image as ImageIcon, Info } from 'lucide-react';

type PixelateMode = 'standard' | 'diamond' | 'circle';
type RegionMode = 'full' | 'center';
type OutputFormat = 'png' | 'jpg';

interface Preset {
  label: string;
  value: number;
}

const PRESETS: Preset[] = [
  { label: 'Subtle', value: 4 },
  { label: 'Medium', value: 10 },
  { label: 'Strong', value: 25 },
  { label: 'Extreme', value: 50 },
];

const MODES: { value: PixelateMode; label: string; description: string }[] = [
  { value: 'standard', label: 'Standard', description: 'Square pixel blocks' },
  { value: 'diamond', label: 'Diamond', description: 'Rotated 45-degree squares' },
  { value: 'circle', label: 'Circle', description: 'Circular dots with background' },
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const PixelateImage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [pixelSize, setPixelSize] = useState(10);
  const [mode, setMode] = useState<PixelateMode>('standard');
  const [regionMode, setRegionMode] = useState<RegionMode>('full');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [jpgQuality, setJpgQuality] = useState(92);
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ w: number; h: number } | null>(null);
  const [showBeforeAfter, setShowBeforeAfter] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    setFile(f);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setOriginalPreview(url);
      const img = new window.Image();
      img.onload = () => {
        setImgElement(img);
        setImageDimensions({ w: img.naturalWidth, h: img.naturalHeight });
      };
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const applyPixelate = useCallback((canvas: HTMLCanvasElement, img: HTMLImageElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = img.naturalWidth;
    const h = img.naturalHeight;
    canvas.width = w;
    canvas.height = h;

    // Draw original first
    ctx.drawImage(img, 0, 0);

    const imageData = ctx.getImageData(0, 0, w, h);
    const data = imageData.data;

    const size = pixelSize;

    // Determine region bounds
    let rx = 0, ry = 0, rw = w, rh = h;
    if (regionMode === 'center') {
      rx = Math.floor(w * 0.25);
      ry = Math.floor(h * 0.25);
      rw = Math.floor(w * 0.5);
      rh = Math.floor(h * 0.5);
    }

    // Clear and redraw original
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0);

    if (mode === 'standard') {
      for (let y = ry; y < ry + rh; y += size) {
        for (let x = rx; x < rx + rw; x += size) {
          const cx = Math.min(x + Math.floor(size / 2), w - 1);
          const cy = Math.min(y + Math.floor(size / 2), h - 1);
          const idx = (cy * w + cx) * 4;
          const r = data[idx], g = data[idx + 1], b = data[idx + 2];
          ctx.fillStyle = `rgb(${r},${g},${b})`;
          const bw = Math.min(size, rx + rw - x);
          const bh = Math.min(size, ry + rh - y);
          ctx.fillRect(x, y, bw, bh);
        }
      }
    } else if (mode === 'diamond') {
      // Draw pixelated region with diamond shapes
      for (let y = ry; y < ry + rh; y += size) {
        for (let x = rx; x < rx + rw; x += size) {
          const cx = Math.min(x + Math.floor(size / 2), w - 1);
          const cy = Math.min(y + Math.floor(size / 2), h - 1);
          const idx = (cy * w + cx) * 4;
          const r = data[idx], g = data[idx + 1], b = data[idx + 2];
          ctx.fillStyle = `rgb(${r},${g},${b})`;
          const half = size / 2;
          ctx.save();
          ctx.translate(x + half, y + half);
          ctx.rotate(Math.PI / 4);
          ctx.fillRect(-half * 0.7, -half * 0.7, size * 0.7, size * 0.7);
          ctx.restore();
        }
      }
    } else if (mode === 'circle') {
      // Fill background for region first with averaged color
      ctx.fillStyle = '#000';
      ctx.fillRect(rx, ry, rw, rh);
      for (let y = ry; y < ry + rh; y += size) {
        for (let x = rx; x < rx + rw; x += size) {
          const cx = Math.min(x + Math.floor(size / 2), w - 1);
          const cy = Math.min(y + Math.floor(size / 2), h - 1);
          const idx = (cy * w + cx) * 4;
          const r = data[idx], g = data[idx + 1], b = data[idx + 2];
          ctx.fillStyle = `rgb(${r},${g},${b})`;
          ctx.beginPath();
          ctx.arc(x + size / 2, y + size / 2, size / 2 - 1, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
  }, [pixelSize, mode, regionMode]);

  useEffect(() => {
    if (!imgElement || !previewCanvasRef.current) return;
    applyPixelate(previewCanvasRef.current, imgElement);
  }, [imgElement, applyPixelate]);

  const handleDownload = useCallback(() => {
    if (!imgElement) return;
    const canvas = document.createElement('canvas');
    applyPixelate(canvas, imgElement);
    const mimeType = outputFormat === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = outputFormat === 'jpg' ? jpgQuality / 100 : undefined;
    const dataUrl = canvas.toDataURL(mimeType, quality);
    const link = document.createElement('a');
    const baseName = file?.name?.replace(/\.[^.]+$/, '') || 'pixelated';
    link.download = `${baseName}-pixelated.${outputFormat === 'jpg' ? 'jpg' : 'png'}`;
    link.href = dataUrl;
    link.click();
  }, [imgElement, applyPixelate, outputFormat, jpgQuality, file]);

  const handleReset = useCallback(() => {
    setFile(null);
    setOriginalPreview(null);
    setImgElement(null);
    setImageDimensions(null);
    setPixelSize(10);
    setMode('standard');
    setRegionMode('full');
    setError(null);
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Grid3X3 className="w-6 h-6 text-purple-600" />
          <h1 className="text-2xl font-bold text-gray-900">Pixelate Image</h1>
        </div>
        <p className="text-gray-600">Apply pixelation effects with multiple modes and region options.</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {!file ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-colors"
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-1">Drop an image here or click to upload</p>
          <p className="text-sm text-gray-500">Supports PNG, JPG, WebP, GIF</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Controls */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Settings</h2>
              <button onClick={handleReset} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
                <RotateCcw className="w-4 h-4" /> Reset
              </button>
            </div>

            {/* Mode selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pixelate Mode</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {MODES.map((m) => (
                  <button
                    key={m.value}
                    onClick={() => setMode(m.value)}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      mode === m.value
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <div className="font-medium text-sm">{m.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{m.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Pixel size slider */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Pixel Size</label>
                <span className="text-sm font-mono text-purple-600">{pixelSize}px</span>
              </div>
              <input
                type="range"
                min={2}
                max={50}
                value={pixelSize}
                onChange={(e) => setPixelSize(Number(e.target.value))}
                className="w-full accent-purple-600"
              />
              <div className="flex gap-2 mt-2">
                {PRESETS.map((p) => (
                  <button
                    key={p.label}
                    onClick={() => setPixelSize(p.value)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      pixelSize === p.value
                        ? 'bg-purple-100 border-purple-300 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Region selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Region</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setRegionMode('full')}
                  className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                    regionMode === 'full'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  Full Image
                </button>
                <button
                  onClick={() => setRegionMode('center')}
                  className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                    regionMode === 'center'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  Center Only (Face Blur)
                </button>
              </div>
              {regionMode === 'center' && (
                <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                  <Info className="w-3 h-3" />
                  <span>Pixelates the center 50% of the image, leaving edges sharp.</span>
                </div>
              )}
            </div>

            {/* Output format */}
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Output Format</label>
                <select
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value as OutputFormat)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="png">PNG</option>
                  <option value="jpg">JPG</option>
                </select>
              </div>
              {outputFormat === 'jpg' && (
                <div className="flex-1 min-w-[150px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quality: {jpgQuality}%
                  </label>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    value={jpgQuality}
                    onChange={(e) => setJpgQuality(Number(e.target.value))}
                    className="w-full accent-purple-600"
                  />
                </div>
              )}
            </div>

            {/* Before/After toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="beforeAfter"
                checked={showBeforeAfter}
                onChange={(e) => setShowBeforeAfter(e.target.checked)}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <label htmlFor="beforeAfter" className="text-sm text-gray-700">Show before/after side-by-side</label>
            </div>
          </div>

          {/* Image info */}
          {imageDimensions && file && (
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1"><ImageIcon className="w-4 h-4" /> {imageDimensions.w} x {imageDimensions.h}px</span>
              <span>{formatFileSize(file.size)}</span>
              <span>{file.name}</span>
            </div>
          )}

          {/* Preview */}
          <div className={`grid gap-4 ${showBeforeAfter ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
            {showBeforeAfter && originalPreview && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Original</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                  <img src={originalPreview} alt="Original" className="w-full h-auto" />
                </div>
              </div>
            )}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Pixelated</h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                <canvas ref={previewCanvasRef} className="w-full h-auto" />
              </div>
            </div>
          </div>

          {/* Download button */}
          <div className="flex gap-3">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              <Download className="w-5 h-5" /> Download Pixelated Image
            </button>
          </div>

          <canvas ref={canvasRef} className="hidden" />
        </div>
      )}
    </div>
  );
};

export default PixelateImage;
